import { useState, useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container } from "../components/Container";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { LoadingContainer } from "../components/LoadingContainer";
import { InfoBox } from "../components/InfoBox";
import { SidebarPanel } from "../components/SidebarPanel";
import { Modal } from "../components/Modal";
import { FormActions } from "../components/FormActions";
import { ResellerAvailableBillingItemForm } from "./components/ResellerAvailableBillingItemForm";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../components/Table";
import {
  useGetResellerAvailableBillingItemsQuery,
  useCreateResellerAvailableBillingItemMutation,
  useUpdateResellerAvailableBillingItemMutation,
  useDeleteResellerAvailableBillingItemMutation,
  useSetResellerAvailableBillingItemsMutation,
} from "../query/resellerAvailableBillingItemsQuery";
import { useGetAvailableBillingItemsQuery } from "../query/availableBillingItemsQuery";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type { AvailableBillingItemId, ResellerId } from "@KassaroAB/shared/types/ids";
import type { ResellerAvailableBillingItem, RestCreateResellerAvailableBillingItem, RestUpdateResellerAvailableBillingItem } from "@KassaroAB/shared/types/resellerAvailableBillingItem";
import type { MultiCurrencyMoney } from "@KassaroAB/shared/types/multiCurrencyMoney";
import { formatMoney } from "../utils/formatMoney";

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.x4}px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: ${fontSizes.xlarge}px;
  font-weight: 600;
  color: ${colors.text};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${spacing.x2}px;
`;

const BulkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
  max-height: 420px;
  overflow-y: auto;
`;

const BulkRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
  cursor: pointer;
`;

const DeleteButton = styled(Button)`
  background-color: ${colors.danger};
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

const formatPrices = (price?: MultiCurrencyMoney): string => {
  if (!price || !price.currencies) return "-";
  return Object.values(price.currencies)
    .filter(Boolean)
    .map((money) => formatMoney(money!))
    .join(", ") || "-";
};

export const ResellerAvailableBillingItemsView = () => {
  const { t } = useTranslation();
  const { resellerId } = useParams<{ resellerId: ResellerId }>();

  const formatPricing = (pricing?: ResellerAvailableBillingItem["pricing"]): string => {
    if (!pricing) return "-";
    const prices: string[] = [];
    if (pricing.monthlyPrice) {
      const formatted = formatPrices(pricing.monthlyPrice);
      if (formatted !== "-")
        prices.push(`${t("availableBillingItemsView.pricing.monthly")}: ${formatted}`);
    }
    if (pricing.oneTimePrice) {
      const formatted = formatPrices(pricing.oneTimePrice);
      if (formatted !== "-")
        prices.push(`${t("availableBillingItemsView.pricing.oneTime")}: ${formatted}`);
    }
    if (pricing.prepaid36MonthPrice) {
      const formatted = formatPrices(pricing.prepaid36MonthPrice);
      if (formatted !== "-")
        prices.push(`${t("availableBillingItemsView.pricing.prepaid36")}: ${formatted}`);
    }
    return prices.length > 0 ? prices.join(" | ") : "-";
  };
  const [selectedResellerAvailableBillingItemId, setSelectedResellerAvailableBillingItemId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: resellerAvailableBillingItems,
    isLoading: isLoadingResellerItems,
    isSuccess: isResellerItemsSuccess,
    error: resellerItemsError,
  } = useGetResellerAvailableBillingItemsQuery(resellerId ?? null);

  const {
    data: allAvailableBillingItems,
    isLoading: isLoadingAllItems,
    isSuccess: isAllItemsSuccess,
    error: allItemsError,
  } = useGetAvailableBillingItemsQuery();

  const { mutate: createResellerItem, error: createError } =
    useCreateResellerAvailableBillingItemMutation(resellerId ?? null);
  const { mutate: updateResellerItem, error: updateError } =
    useUpdateResellerAvailableBillingItemMutation(resellerId ?? null);
  const { mutate: deleteResellerItem, error: deleteError } =
    useDeleteResellerAvailableBillingItemMutation(resellerId ?? null);
  const {
    mutate: setAllResellerItems,
    isPending: isSetAllPending,
    error: setAllError,
  } = useSetResellerAvailableBillingItemsMutation(resellerId ?? null);
  const canBulkReplace = isResellerItemsSuccess && isAllItemsSuccess;

  const bulkForm = useForm({
    defaultValues: {
      selectedIds: [] as AvailableBillingItemId[],
    },
    onSubmit: ({ value }) => {
      setAllResellerItems(
        value.selectedIds.map((availableBillingItemId) => ({
          id: availableBillingItemId,
        })),
        {
          onSuccess: () => {
            setSuccessMessage(
              t("resellerAvailableBillingItemsView.successUpdated")
            );
            setIsBulkOpen(false);
          },
        }
      );
    },
  });
  const bulkSelectedIds = useStore(
    bulkForm.store,
    (state) => state.values.selectedIds
  );

  const selectedResellerItem = resellerAvailableBillingItems?.find(
    (item) => item.id === selectedResellerAvailableBillingItemId
  );

  const availableCatalogItems = (allAvailableBillingItems ?? []).filter(
    (item) => item.archivedAt == null
  );

  const availableItemsToAdd = availableCatalogItems.filter(
    (item) => !resellerAvailableBillingItems?.some(
      (resellerItem) => resellerItem.id === item.id
    )
  );

  const handleResellerItemClick = (item: ResellerAvailableBillingItem) => {
    setSelectedResellerAvailableBillingItemId(item.id);
    setIsCreating(false);
    setIsPanelOpen(true);
  };

  const handleCreateClick = () => {
    setSelectedResellerAvailableBillingItemId(null);
    setIsCreating(true);
    setIsPanelOpen(true);
  };

  const handleCreate = (data: RestCreateResellerAvailableBillingItem) => {
    createResellerItem(data, {
      onSuccess: () => {
        setSuccessMessage(t("resellerAvailableBillingItemsView.successAdded"));
        setIsPanelOpen(false);
        setIsCreating(false);
        setSelectedResellerAvailableBillingItemId(null);
      },
      onError: () => {
        setSuccessMessage(null);
      },
    });
  };

  const handleUpdate = (data: RestUpdateResellerAvailableBillingItem) => {
    if (!selectedResellerAvailableBillingItemId) return;
    updateResellerItem(
      {
        resellerAvailableBillingItemId: selectedResellerAvailableBillingItemId,
        data: data,
      },
      {
        onSuccess: () => {
          setSuccessMessage(t("resellerAvailableBillingItemsView.successUpdated"));
          setIsPanelOpen(false);
          setSelectedResellerAvailableBillingItemId(null);
        },
        onError: () => {
          setSuccessMessage(null);
        },
      },
    );
  };

  const handleDelete = (resellerAvailableBillingItemId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(t("resellerAvailableBillingItemsView.deleteConfirm"))) {
      deleteResellerItem(resellerAvailableBillingItemId, {
        onSuccess: () => {
          setSuccessMessage(t("resellerAvailableBillingItemsView.successRemoved"));
        },
        onError: () => {
          setSuccessMessage(null);
        },
      });
    }
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setIsCreating(false);
    setSelectedResellerAvailableBillingItemId(null);
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (isLoadingResellerItems || isLoadingAllItems) {
    return (
      <Container>
        <PageHeader>
          <Title>{t("resellerAvailableBillingItemsView.title")}</Title>
        </PageHeader>
        <LoadingContainer message={t("resellerAvailableBillingItemsView.loading")} />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader>
        <Title>{t("resellerAvailableBillingItemsView.title")}</Title>
        <HeaderActions>
          <Button
            $color="secondary"
            disabled={!canBulkReplace}
            onClick={() => {
              bulkForm.reset({
                selectedIds: (resellerAvailableBillingItems ?? []).map(
                  (item) => item.id
                ),
              });
              setIsBulkOpen(true);
            }}
          >
            {t("resellerAvailableBillingItemsView.bulkEnableButton")}
          </Button>
          <Button onClick={handleCreateClick} disabled={availableItemsToAdd.length === 0}>
            {t("resellerAvailableBillingItemsView.addButton")}
          </Button>
        </HeaderActions>
      </PageHeader>

      {(resellerItemsError || allItemsError) && (
        <InfoBox variant="error">
          {t("resellerAvailableBillingItemsView.errorLoad")}
        </InfoBox>
      )}

      {(createError || updateError || deleteError || setAllError) && (
        <InfoBox variant="error">
          {t("resellerAvailableBillingItemsView.errorUpdate")}
        </InfoBox>
      )}

      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}

      {resellerAvailableBillingItems && resellerAvailableBillingItems.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("resellerAvailableBillingItemsView.tableName")}</TableHeaderCell>
                <TableHeaderCell>{t("resellerAvailableBillingItemsView.tableType")}</TableHeaderCell>
                <TableHeaderCell>{t("resellerAvailableBillingItemsView.tableDescription")}</TableHeaderCell>
                <TableHeaderCell>{t("resellerAvailableBillingItemsView.tablePricing")}</TableHeaderCell>
                <TableHeaderCell>{t("resellerAvailableBillingItemsView.tableActions")}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {resellerAvailableBillingItems.map((item) => (
                <TableRow
                  key={item.id}
                  clickable
                  onClick={() => handleResellerItemClick(item)}
                >
                  <TableCell noWrap>{item.name}</TableCell>
                  <TableCell noWrap>{t(`billingItems.type.${item.type}`)}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell noWrap>{formatPricing(item.pricing)}</TableCell>
                  <TableCell noWrap>
                    <DeleteButton onClick={(e) => handleDelete(item.id, e)}>
                      {t("resellerAvailableBillingItemsView.remove")}
                    </DeleteButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <div style={{ textAlign: "center", color: colors.textMuted }}>
            {t("resellerAvailableBillingItemsView.empty")}
          </div>
        </Card>
      )}

      {isBulkOpen && (
        <Modal
          isOpen
          onClose={() => setIsBulkOpen(false)}
          title={t("resellerAvailableBillingItemsView.bulkEnableTitle")}
        >
          <BulkList>
            {availableCatalogItems.map((item) => (
              <BulkRow key={item.id}>
                <input
                  type="checkbox"
                  checked={bulkSelectedIds.includes(item.id)}
                  onChange={() =>
                    bulkForm.setFieldValue(
                      "selectedIds",
                      bulkSelectedIds.includes(item.id)
                        ? bulkSelectedIds.filter(
                            (selectedId) => selectedId !== item.id
                          )
                        : [...bulkSelectedIds, item.id]
                    )
                  }
                />
                <span>
                  {item.name} ({t(`billingItems.type.${item.type}`)})
                </span>
              </BulkRow>
            ))}
          </BulkList>
          {setAllError && (
            <InfoBox variant="error">
              {t("resellerAvailableBillingItemsView.errorUpdate")}
            </InfoBox>
          )}
          <FormActions>
            <Button
              type="button"
              $color="secondary"
              onClick={() => setIsBulkOpen(false)}
              disabled={isSetAllPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              disabled={isSetAllPending || !canBulkReplace}
              onClick={() => bulkForm.handleSubmit()}
            >
              {isSetAllPending
                ? t("common.saving")
                : t("common.saveChanges")}
            </Button>
          </FormActions>
        </Modal>
      )}

      {(selectedResellerItem || isCreating) && (
        <SidebarPanel
          isOpen={isPanelOpen}
          onClose={handlePanelClose}
          title={
            isCreating
              ? t("resellerAvailableBillingItemsView.panelAdd")
              : t("resellerAvailableBillingItemsView.panelEdit")
          }
        >
          <ResellerAvailableBillingItemForm
            availableBillingItems={isCreating ? availableItemsToAdd : availableCatalogItems}
            resellerAvailableBillingItem={selectedResellerItem}
            onSubmit={selectedResellerItem ? handleUpdate : handleCreate}
            onCancel={handlePanelClose}
          />
        </SidebarPanel>
      )}
    </Container>
  );
};
